import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Upload, Database, HardDrive, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';

const BackupRestore = () => {
  const { isAdmin, isAdminOnly } = useAuth();
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchBackupInfo(); }, []);

  const fetchBackupInfo = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/admin/backup/info');
      setBackupInfo(resp.data);
    } catch { toast.error('Failed to fetch backup info'); }
    finally { setLoading(false); }
  };

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      const resp = await api.get('/admin/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `WWAGS_MIS_Backup_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.json`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
    } catch { toast.error('Failed to download backup'); }
    finally { setDownloading(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Please select a .json backup file');
        return;
      }
      setSelectedFile(file);
      setConfirmRestore(false);
      setRestoreResult(null);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) { toast.error('Please select a backup file first'); return; }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const resp = await api.post('/admin/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRestoreResult(resp.data);
      toast.success('Database restored successfully!');
      fetchBackupInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoring(false);
      setConfirmRestore(false);
    }
  };

  const collectionLabels = {
    users: 'Users',
    branches: 'Branches',
    entrepreneurs: 'Entrepreneurs',
    staff: 'Staff',
    loans: 'Loans',
    loan_collections: 'Loan Collections',
    loan_masters: 'Loan Masters',
    fund_receipts: 'Fund Receipts',
    expenditures: 'Expenditures',
    assets: 'Assets',
    activity_logs: 'Activity Logs',
    fund_heads: 'Fund Heads',
    counters: 'Counters',
  };

  if (!isAdminOnly()) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-red-300" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
        <p className="text-slate-500">Only Admin users can access Backup & Restore.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="backup-restore-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Backup & Restore</h1>
          <p className="text-sm text-slate-600">Download complete database backup or restore from a previous backup</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Backup Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-emerald-700 px-5 py-3 flex items-center gap-2">
            <Download className="w-5 h-5 text-white" />
            <h2 className="text-sm font-bold text-white tracking-wide">DOWNLOAD BACKUP</h2>
          </div>
          <div className="p-5">
            <p className="text-sm text-slate-600 mb-4">
              Download a complete backup of all portal data as a JSON file to your local drive. 
              This includes all users, branches, entrepreneurs, staff, loans, collections, funds, expenditures, and assets.
            </p>

            {/* Current Database Stats */}
            {backupInfo && (
              <div className="mb-5">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Current Database</h3>
                <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-100"><th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Collection</th><th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Records</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {Object.entries(backupInfo.collections || {}).filter(([,v]) => v > 0).map(([key, val]) => (
                        <tr key={key}>
                          <td className="px-4 py-2 text-slate-700">{collectionLabels[key] || key}</td>
                          <td className="px-4 py-2 text-right font-bold text-slate-900">{val}</td>
                        </tr>
                      ))}
                      <tr className="bg-emerald-50 font-bold">
                        <td className="px-4 py-2 text-emerald-800">Total Records</td>
                        <td className="px-4 py-2 text-right text-emerald-800">{backupInfo.total_records}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Button
              onClick={handleDownloadBackup}
              disabled={downloading}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-11"
              data-testid="download-backup-btn"
            >
              {downloading ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Preparing backup...</>
              ) : (
                <><HardDrive className="w-4 h-4 mr-2" />Download Complete Backup</>
              )}
            </Button>
          </div>
        </div>

        {/* Restore Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-amber-600 px-5 py-3 flex items-center gap-2">
            <Upload className="w-5 h-5 text-white" />
            <h2 className="text-sm font-bold text-white tracking-wide">RESTORE FROM BACKUP</h2>
          </div>
          <div className="p-5">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-800">Warning</p>
                <p className="text-xs text-red-700">Restoring a backup will <b>replace ALL existing data</b> in the portal with the backup data. This action cannot be undone. Make sure to download a fresh backup before restoring.</p>
              </div>
            </div>

            {/* File Selection */}
            <div className="mb-4">
              <input
                type="file"
                accept=".json"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-11 border-dashed border-2"
                data-testid="select-backup-file-btn"
              >
                <Database className="w-4 h-4 mr-2" />
                {selectedFile ? selectedFile.name : 'Select Backup File (.json)'}
              </Button>
              {selectedFile && (
                <p className="text-xs text-slate-500 mt-2">
                  File size: {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            {/* Confirm & Restore */}
            {selectedFile && !confirmRestore && (
              <Button
                onClick={() => setConfirmRestore(true)}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white h-11"
                data-testid="initiate-restore-btn"
              >
                <Upload className="w-4 h-4 mr-2" />Restore from this Backup
              </Button>
            )}

            {confirmRestore && (
              <div className="bg-red-50 border border-red-300 rounded-lg p-4 space-y-3">
                <p className="text-sm font-bold text-red-800">Are you absolutely sure?</p>
                <p className="text-xs text-red-700">All current data will be permanently replaced. Type "RESTORE" below and click confirm.</p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleRestore}
                    disabled={restoring}
                    className="bg-red-700 hover:bg-red-800 text-white flex-1"
                    data-testid="confirm-restore-btn"
                  >
                    {restoring ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Restoring...</>
                    ) : 'Yes, Restore Now'}
                  </Button>
                  <Button variant="outline" onClick={() => setConfirmRestore(false)} className="flex-1">Cancel</Button>
                </div>
              </div>
            )}

            {/* Restore Result */}
            {restoreResult && (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-sm font-bold text-emerald-800">Restore Completed</h3>
                </div>
                {restoreResult.backup_info && (
                  <p className="text-xs text-slate-600 mb-2">
                    Backup created: {new Date(restoreResult.backup_info.created_at).toLocaleString('en-IN')} by {restoreResult.backup_info.created_by}
                  </p>
                )}
                <div className="bg-white rounded border border-emerald-100 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-emerald-100"><th className="px-3 py-1.5 text-left">Collection</th><th className="px-3 py-1.5 text-right">Records Restored</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {Object.entries(restoreResult.restored || {}).map(([key, val]) => (
                        <tr key={key}><td className="px-3 py-1.5">{collectionLabels[key] || key}</td><td className="px-3 py-1.5 text-right font-bold">{val}</td></tr>
                      ))}
                      <tr className="bg-emerald-50 font-bold"><td className="px-3 py-1.5">Total</td><td className="px-3 py-1.5 text-right">{restoreResult.total_records_restored}</td></tr>
                    </tbody>
                  </table>
                </div>
                {Object.keys(restoreResult.errors || {}).length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    Errors: {JSON.stringify(restoreResult.errors)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupRestore;
